import { motion } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  Building,
  Droplets,
  FileText,
  GraduationCap,
  Home,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const DirectoryDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [directoryEntry, setDirectoryEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;

  useEffect(() => {
    const fetchDirectoryDetails = async () => {
      try {
        setLoading(true);
        const data = await api.directory.getById(id);
        setDirectoryEntry(data);
      } catch (error) {
        console.error("Error fetching directory details:", error);
        setError("Failed to load directory details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDirectoryDetails();
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHome = () => {
    navigate("/home");
  };

  const handleBackToDirectory = () => {
    navigate(-1);
  };

  const formatBloodGroup = (bloodGroup) => {
    if (!bloodGroup) return "Not specified";
    return bloodGroup;
  };

  const getBloodGroupColor = (bloodGroup) => {
    if (!bloodGroup) return "bg-gray-500";

    if (bloodGroup.includes("+")) {
      return "bg-red-500";
    } else if (bloodGroup.includes("-")) {
      return "bg-blue-500";
    }
    return "bg-purple-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-emerald-600" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-emerald-700">Loading directory details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center text-emerald-800">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!directoryEntry) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center text-emerald-800">
          <h2 className="text-2xl font-bold mb-4">Directory Entry Not Found</h2>
          <p className="mb-4">
            The requested directory entry could not be found.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 md:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-emerald-900 mb-2">
              Directory Member Details
            </h1>
            <p className="text-emerald-600">
              KGC'97 Day 2026 - {directoryEntry.batch} Batch
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleBackToDirectory}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <button
              onClick={handleHome}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Home size={18} />
              Home
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card - Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 h-full">
              {/* Profile Image */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative">
                  {directoryEntry.imageUrl ? (
                    <img
                      src={`${API_BASE_URL}${directoryEntry.imageUrl}`}
                      alt={directoryEntry.name}
                      className="w-48 h-48 rounded-full object-cover border-4 border-emerald-100 shadow-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          directoryEntry.name
                        )}&background=10b981&color=fff&size=192`;
                      }}
                    />
                  ) : (
                    <div className="w-48 h-48 rounded-full bg-emerald-100 flex items-center justify-center border-4 border-emerald-200">
                      <User className="text-emerald-600" size={80} />
                    </div>
                  )}
                </div>

                <div className="text-center mt-6">
                  <h2 className="text-2xl font-bold text-emerald-900 mb-2">
                    {directoryEntry.name}
                  </h2>
                  <div className="flex items-center justify-center gap-2">
                    <GraduationCap size={16} className="text-emerald-600" />
                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                      {directoryEntry.batch} Batch
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Phone size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">Mobile</p>
                    <p className="font-medium text-emerald-900">
                      {directoryEntry.mobile}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Mail size={18} className="text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-emerald-600">Email</p>
                    <p className="font-medium text-emerald-900 break-all">
                      {directoryEntry.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Droplets size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">Blood Group</p>
                    <span
                      className={`px-3 py-1 rounded-full text-white font-medium ${getBloodGroupColor(
                        directoryEntry.bloodGroup
                      )}`}
                    >
                      {formatBloodGroup(directoryEntry.bloodGroup)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Details Card - Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 h-full">
              <h3 className="text-xl font-bold text-emerald-900 mb-6 pb-3 border-b border-emerald-100">
                Professional & Personal Details
              </h3>

              <div className="space-y-6">
                {/* Profession Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase size={20} className="text-emerald-600" />
                    <h4 className="text-lg font-semibold text-emerald-800">
                      Profession
                    </h4>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 min-w-0">
                    <p className="text-emerald-900 break-words whitespace-normal">
                      {directoryEntry.profession || "Not specified"}
                    </p>
                  </div>
                </div>

                {/* Professional Details Section */}
                {directoryEntry.professionalDetails && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={20} className="text-emerald-600" />
                      <h4 className="text-lg font-semibold text-emerald-800">
                        Professional Details
                      </h4>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 min-w-0">
                      <p className="text-emerald-900 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                        {directoryEntry.professionalDetails}
                      </p>
                    </div>
                  </div>
                )}

                {/* Addresses Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Office Address */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building size={20} className="text-emerald-600" />
                      <h4 className="text-lg font-semibold text-emerald-800">
                        Present Address
                      </h4>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 min-h-[120px] min-w-0">
                      {directoryEntry.officeAddress ? (
                        <p className="text-emerald-900 break-words whitespace-normal">
                          {directoryEntry.officeAddress}
                        </p>
                      ) : (
                        <p className="text-emerald-500 italic">Not specified</p>
                      )}
                    </div>
                  </div>

                  {/* Permanent Address */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={20} className="text-emerald-600" />
                      <h4 className="text-lg font-semibold text-emerald-800">
                        Permanent Address
                      </h4>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 min-h-[120px] min-w-0">
                      {directoryEntry.permanentAddress ? (
                        <p className="text-emerald-900 break-words whitespace-normal">
                          {directoryEntry.permanentAddress}
                        </p>
                      ) : (
                        <p className="text-emerald-500 italic">Not specified</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center text-emerald-600 text-sm max-w-7xl mx-auto"
        >
          <div className="bg-white/80 rounded-xl p-4 shadow-sm">
            <p className="mb-2">
              This information is part of the KGC'97 Alumni Directory.
            </p>
            <p>
              For any updates or corrections, please contact the directory
              administrator.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DirectoryDetailsPage;
